import React, { useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import ReactGA from "react-ga4";
import Header from "../layouts/Header/Header";
import Footer from "../layouts/Footer/Footer";
import AboutUsPage from "../pages/AboutUsPage/AboutUsPage";
import ContactUsPage from "../pages/ContactUsPage/ContactUsPage";
import {
  PoliciesCookies,
  PoliciesPrivacy,
  PoliciesTerms,
} from "../pages/Policies/Policies";
import HomePage from "../pages/HomePage/HomePage";
import EventPageDynamic from "../pages/EventPage/EventPageDynamic";
import ConferenceRegistrationForm from "../pages/EventPage/ConferenceRegistrationForm";
import AuthPage from "../pages/AuthPage/AuthPage";
import UserAuth from "../pages/AuthPage/UserAuth";
import ForgotPassword from "../pages/AuthPage/ForgotPassword";
import HostFormPage from "../pages/HostFormPage/HostFormPage";
import UserDashboardLayout from "../pages/UserPage/UserDashboardLayout";
import UserPageDashboard from "../pages/UserPage/UserPageDashboard";
import UserPageOrders from "../pages/UserPage/UserPageOrders";
import UserPagePreferences from "../pages/UserPage/UserPagePreferences";
import UserPagePersonalDetails from "../pages/UserPage/UserPagePersonalDetails";
import UserPageEventAdmin from "../pages/UserPage/UserPageEventAdmin";
import UserPageManageEvents from "../pages/UserPage/UserPageManageEvents";
import UserPageEventForm from "../pages/UserPage/UserPageEventForm";
import EventAdminPanel from "../pages/UserPage/EventAdminPanel";
import UserPageSuperAdmin from "../pages/UserPage/UserPageSuperAdmin";
import UserPageAdministration from "../pages/UserPage/UserPageAdministration";
import UserPagePaperVideos from "../pages/UserPage/UserPagePaperVideos";
import PaperVideoPage from "../pages/PaperVideoPage/PaperVideoPage";
import { GlobalContext } from "../context/globalContext";
import AlertWindow from "../components/AlertWindow/AlertWindow";
import CheckoutSuccessPage from "../pages/CheckoutPage/CheckoutSuccessPage";
import CheckoutCancelledPage from "../pages/CheckoutPage/CheckoutCancelledPage";
import Cookie from "../layouts/Cookie/Cookie";

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <Content />
    </BrowserRouter>
  );
}

function Content() {
  const { customAlert, analytics } = useContext(GlobalContext);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Send a GA pageview on every route change, but only when the user has
    // accepted analytics cookies AND GA has been initialised (REACT_APP_GA_ID).
    if (analytics && process.env.REACT_APP_GA_ID) {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname + location.search,
      });
    }
  }, [location, analytics]);
  return (
    <div className="flex min-h-dvh flex-col">
      <Header />
      <main className="flex-1 bg-slate-50 pt-24 px-6 text-slate-800">
        <Routes>
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route
            path="/paper-videos"
            element={
              <>
                <Helmet>
                  <title>Paper Presentation Video | Events House</title>
                </Helmet>
                <PaperVideoPage />
              </>
            }
          />
          <Route
            path="/login"
            element={
              <>
                <Helmet>
                  <title>Login | Events House</title>
                </Helmet>
                <AuthPage page="login" />
              </>
            }
          />
          <Route
            path="/signup"
            element={
              <>
                <Helmet>
                  <title>Signup | Events House</title>
                </Helmet>
                <AuthPage page="register" />
              </>
            }
          />
          <Route
            path="/user/auth"
            element={
              <>
                <Helmet>
                  <title>User | Events House</title>
                </Helmet>
                <UserAuth />
              </>
            }
          />
          <Route path="/user/personal-details" element={<Navigate to="/user/account" replace />} />
          <Route path="/user" element={<UserDashboardLayout />}>
            <Route index element={<Navigate to="/user/dashboard" replace />} />
            <Route
              path="dashboard"
              element={
                <>
                  <Helmet>
                    <title>Dashboard | Events House</title>
                  </Helmet>
                  <UserPageDashboard />
                </>
              }
            />
            <Route
              path="orders"
              element={
                <>
                  <Helmet>
                    <title>Orders &amp; Registrations | Events House</title>
                  </Helmet>
                  <UserPageOrders />
                </>
              }
            />
            <Route
              path="events/new"
              element={
                <>
                  <Helmet>
                    <title>Create Event | Events House</title>
                  </Helmet>
                  <UserPageEventForm />
                </>
              }
            />
            <Route
              path="events/:id/edit"
              element={
                <>
                  <Helmet>
                    <title>Edit Event | Events House</title>
                  </Helmet>
                  <UserPageEventForm />
                </>
              }
            />
            <Route
              path="events"
              element={
                <>
                  <Helmet>
                    <title>Manage Events | Events House</title>
                  </Helmet>
                  <UserPageManageEvents />
                </>
              }
            />
            <Route
              path="event-admin/events/:eventId"
              element={
                <>
                  <Helmet>
                    <title>Event Admin Panel | Events House</title>
                  </Helmet>
                  <EventAdminPanel />
                </>
              }
            />
            <Route
              path="event-admin"
              element={
                <>
                  <Helmet>
                    <title>Event Admin | Events House</title>
                  </Helmet>
                  <UserPageEventAdmin />
                </>
              }
            />
            <Route
              path="administration"
              element={
                <>
                  <Helmet>
                    <title>Administration | Events House</title>
                  </Helmet>
                  <UserPageAdministration />
                </>
              }
            />
            <Route
              path="paper-videos"
              element={
                <>
                  <Helmet>
                    <title>Paper Videos | Events House</title>
                  </Helmet>
                  <UserPagePaperVideos />
                </>
              }
            />
            <Route
              path="super-admin"
              element={
                <>
                  <Helmet>
                    <title>Super Admin | Events House</title>
                  </Helmet>
                  <UserPageSuperAdmin />
                </>
              }
            />
            <Route
              path="preferences"
              element={
                <>
                  <Helmet>
                    <title>Preferences | Events House</title>
                  </Helmet>
                  <UserPagePreferences />
                </>
              }
            />
            <Route
              path="account"
              element={
                <>
                  <Helmet>
                    <title>Account | Events House</title>
                  </Helmet>
                  <UserPagePersonalDetails />
                </>
              }
            />
          </Route>
          <Route
            path="/reset-password"
            element={
              <>
                <Helmet>
                  <title>Reset Password | Events House</title>
                </Helmet>
                <ForgotPassword />
              </>
            }
          />
          <Route
            path="/form"
            element={
              <>
                <Helmet>
                  <title>Submit an Event | Events House</title>
                </Helmet>
                <HostFormPage />
              </>
            }
          />
          <Route
            path="/checkout/success"
            element={
              <>
                <Helmet>
                  <title>Payment Successful | Events House</title>
                </Helmet>
                <CheckoutSuccessPage />
              </>
            }
          />
          <Route
            path="/checkout/cancelled"
            element={
              <>
                <Helmet>
                  <title>Payment Cancelled | Events House</title>
                </Helmet>
                <CheckoutCancelledPage />
              </>
            }
          />
          <Route path="/" element={<HomePage />} />
          <Route
            path="/events/:slug/register"
            element={
              <>
                <Helmet>
                  <title>Register | Events House</title>
                </Helmet>
                <ConferenceRegistrationForm />
              </>
            }
          />
          <Route
            path="/policies/terms-and-conditions"
            element={<PoliciesTerms />}
          />
          <Route
            path="/policies/privacy-policy"
            element={<PoliciesPrivacy />}
          />
          <Route
            path="/policies/cookies-policy"
            element={<PoliciesCookies />}
          />
          <Route path="/:slug" element={<EventPageDynamic />} />
        </Routes>
      </main>
      <Cookie />
      <Footer />
      {customAlert && <AlertWindow>{customAlert}</AlertWindow>}
    </div>
  );
}
